import { useSyncExternalStore } from 'react';
import './aviso.css';

/**
 * O aviso: a confirmação curta depois de salvar, criar, convidar ou contratar (#765).
 *
 * "Contrato salvo. A mudança foi para o histórico." Fica 2,8 s e sai sozinho.
 * Um por vez: o novo toma o lugar do anterior, e o relógio recomeça.
 *
 * ── Por que uma função, e não um componente por tela ──────────────────────
 *
 * Quem avisa é quem salvou, e isso acontece dentro de um `onSuccess`, longe da
 * árvore da tela. A área (`AreaDeAvisos`) é montada uma vez, na casca, e
 * qualquer tela chama `avisar` sem precisar de contexto nem de prop.
 *
 * ── A região já existe antes do aviso ─────────────────────────────────────
 *
 * O leitor de tela só anuncia o que muda dentro de uma região viva que já
 * estava na página. Por isso a área fica montada vazia, e o texto entra nela.
 * A `versao` é a `key` do texto: o mesmo aviso duas vezes seguidas é anunciado
 * duas vezes, e não engolido porque o texto não mudou.
 */

/** Quanto tempo o aviso fica na tela, sem contar a entrada e a saída. */
export const PERMANENCIA_DO_AVISO_MS = 2800;

type Estado = { readonly texto: string; readonly visivel: boolean; readonly versao: number };

let estado: Estado = { texto: '', visivel: false, versao: 0 };
let relogio: ReturnType<typeof setTimeout> | undefined;
const ouvintes = new Set<() => void>();

function mudar(proximo: Estado): void {
  estado = proximo;
  for (const ouvinte of ouvintes) ouvinte();
}

/** Mostra o aviso no lugar do que estiver na tela. */
export function avisar(texto: string): void {
  clearTimeout(relogio);
  mudar({ texto, visivel: true, versao: estado.versao + 1 });
  relogio = setTimeout(() => mudar({ ...estado, visivel: false }), PERMANENCIA_DO_AVISO_MS);
}

/** Tira o aviso da tela e para o relógio: para os testes e para a saída da sessão. */
export function limparAvisos(): void {
  clearTimeout(relogio);
  mudar({ texto: '', visivel: false, versao: estado.versao + 1 });
}

function assinar(ouvinte: () => void): () => void {
  ouvintes.add(ouvinte);
  return () => {
    ouvintes.delete(ouvinte);
  };
}

const ler = (): Estado => estado;

/** A área dos avisos: uma por aplicação, montada na casca. */
export function AreaDeAvisos() {
  const atual = useSyncExternalStore(assinar, ler, ler);
  return (
    <div className={atual.visivel ? 'aviso aviso--visivel' : 'aviso'} role="status">
      <span key={atual.versao}>{atual.texto}</span>
    </div>
  );
}
